import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Youtube, Twitter, Mail } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

const PILLARS = [
  "Restaurants", "Chefs", "Food Trucks", "Bakeries", "Caterers",
  "Spirits", "Wine", "Breweries", "Coffee Shops", "Hospitality"
];
const EVENTS = [
  "Tastebuds Food & Wine Festival", "Best of Houston Awards",
  "Chef of the Year", "BBQ Cook-Off", "Cocktail Classic",
  "Restaurant Week", "Black Restaurant Excellence Awards",
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.already ? "You're already on the list!" : "You're in — welcome to Tastebuds.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-sand-900 text-sand-50 mt-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        {/* Newsletter band */}
        <div className="border-b border-sand-50/15 pb-14 mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-testid="newsletter-band">
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ochre-400 mb-3">
              The Tastebuds Dispatch
            </div>
            <h3 className="font-serif font-black text-3xl md:text-4xl leading-[1.05]">
              The best tables, stories &amp; events — in your inbox.
            </h3>
          </div>
          <form onSubmit={subscribe} className="lg:col-span-6 flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="flex-1 bg-transparent border border-sand-50/25 px-5 py-4 text-sand-50 placeholder:text-sand-50/40 focus:border-brand-500 outline-none transition-colors"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="newsletter-submit"
              className="bg-brand-500 text-sand-50 px-8 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-brand-600 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {busy ? "Joining…" : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="md:col-span-5">
            <img
              src="https://customer-assets.emergentagent.com/job_tastebuds-hub/artifacts/2vjwnbkm_Tastbuds%20Food%20Network%20Logo.png"
              alt="Tastebuds Food Network"
              data-testid="footer-logo"
              className="w-56 md:w-64 h-56 md:h-64 object-contain mb-6"
            />
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ochre-400 mb-3">
              8 Markets · Est. 2026
            </div>
            <h2 className="font-serif font-black leading-[0.9] text-5xl md:text-6xl lg:text-7xl">
              Tastebuds
              <br />
              <span className="text-brand-500">Food Network</span>
            </h2>
            <p className="mt-6 max-w-md font-light text-sand-50/70 leading-relaxed">
              Where Food, Culture, Community & Hospitality Meet. The editorial home for
              chefs, restaurants, and bartenders across Houston, Atlanta, Dallas, Los Angeles,
              New Orleans, New York, San Diego & the Golden Triangle of Southeast Texas.
            </p>
            <div className="mt-8 flex gap-4">
              {[Instagram, Youtube, Twitter, Mail].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="w-11 h-11 border border-sand-50/20 flex items-center justify-center hover:bg-brand-500 hover:border-brand-500 transition-colors"
                  aria-label="social"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ochre-400 mb-5">
              Pillars
            </div>
            <ul className="space-y-2.5">
              {PILLARS.map((p) => (
                <li key={p}>
                  <Link
                    to="/directory"
                    className="text-sm text-sand-50/80 hover:text-brand-500 transition-colors editorial-link"
                  >
                    {p}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ochre-400 mb-5">
              Signature Events
            </div>
            <ul className="space-y-2.5">
              {EVENTS.map((e) => (
                <li key={e}>
                  <Link to="/events" className="text-sm text-sand-50/80 hover:text-brand-500 transition-colors editorial-link">
                    {e}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-sand-50/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-sand-50/50 font-mono uppercase tracking-widest">
          <span>© 2026 Tastebuds Food Network. All Rights Reserved.</span>
          <span>8 Markets · One Table</span>
        </div>
      </div>
    </footer>
  );
}
